/** One row of their page's conversation: the bubble or card, its heart, its replies, and the actions beside it. */
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { Attachment, WatchMessage } from "../../../shared/types";
import { usePage } from "./context";
import {
  IcAgain,
  IcCheck,
  IcCopy,
  IcDl,
  IcHeart,
  IcHeartF,
  IcLink,
  IcPause,
  IcPin,
  IcPlay,
  IcPlus,
  IcReply,
  IcCDown,
  IcCUp,
} from "./icons";
import { clockSeconds, useNoteStream } from "../../lib/noteStream";
import { captionAt, mmss } from "../room/talk";
import { clock, type Entry, fileHref, isPickedVideo, isVideoNote, plainText } from "./model";
import { PAPER_LINES, usePaper } from "./paper";

/** A file's badge: PDF, Word, Excel, text, or its own extension. */
export function fileType(name: string): { ext: string; label: string; badge: string } {
  const n = name.toLowerCase();
  if (/\.pdf$/.test(n)) return { ext: "pdf", label: "PDF", badge: "PDF" };
  if (/\.(docx?|rtf|odt)$/.test(n)) return { ext: "doc", label: "Word", badge: "DOC" };
  if (/\.(xlsx?|csv|ods)$/.test(n)) return { ext: "xls", label: "Excel", badge: "XLS" };
  if (/\.(txt|md)$/.test(n)) return { ext: "txt", label: "Text", badge: "TXT" };
  const ext = n.split(".").pop() ?? "";
  return { ext: "txt", label: "File", badge: ext.slice(0, 4).toUpperCase() || "FILE" };
}
const VIDEO_FILE = /\.(mp4|mov|m4v|webm)$/i;

/** The person's face beside their own messages: their photo, or the plus that asks for one. */
function MeFace(): ReactNode {
  const { me, openSheet } = usePage();
  if (me.avatar)
    return (
      <button type="button" aria-label="Change your photo" style={{ borderRadius: "50%" }} onClick={() => openSheet("photo")}>
        <img src={me.avatar} alt={me.first} />
      </button>
    );
  return (
    <button type="button" className="pav-add" aria-label="Add your photo" title="Add a photo" onClick={() => openSheet("photo")}>
      <IcPlus s={16} />
    </button>
  );
}

function HostFace({ alt = "" }: { alt?: string }): ReactNode {
  const { host } = usePage();
  return host.avatar ? <img src={host.avatar} alt={alt} /> : <span className="pav-ini">{host.first.slice(0, 1)}</span>;
}

/** A recorded video note: the host's, with its words when it has been listened to, or one the person sent. */
function VideoNote({ a, mine, local }: { a: Attachment; mine: boolean; local: string | null }): ReactNode {
  const { captionsOn, toggleCaptions } = usePage();
  const ref = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<"idle" | "playing" | "paused" | "done">("idle");
  const [t, setT] = useState(0);
  const [len, setLen] = useState(0);
  const src = a.media_url ?? local;
  // A long note is a stream: it plays through hls.js, and the element gets no src of its own.
  const stream = useNoteStream(ref, src);
  const words = !mine && a.captions?.length ? a.captions : null;
  const toggle = () => {
    const v = ref.current;
    if (!v || !src) return;
    if (state === "playing") {
      v.pause();
      setState("paused");
      return;
    }
    if (state === "done") v.currentTime = 0;
    v.play()
      .then(() => setState("playing"))
      .catch(() => setState("paused"));
  };
  const total = len || clockSeconds(a.duration);
  const label = state === "done" ? "Watched" : "Video note";
  const said = words && captionsOn ? captionAt(words, t) : "";
  return (
    <div
      className={`card vnote${mine ? " mine" : ""}${state === "playing" ? " playing" : ""}${state === "done" ? " done" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Play ${mine ? "your" : "the"} video note${a.duration ? `, ${a.duration}` : ""}`}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
    >
      {a.poster_url ? <img className="poster" src={a.poster_url} alt="" /> : null}
      {src ? (
        <video
          ref={ref}
          src={stream ? undefined : src}
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => setLen(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
          onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
          onEnded={() => setState("done")}
        />
      ) : null}
      <div className="shade" />
      {words ? (
        <div className={`vcap${said ? " has" : ""}`} aria-hidden={!said}>
          <span>{said}</span>
        </div>
      ) : null}
      <span className="vplay">{state === "playing" ? <IcPause s={24} /> : state === "done" ? <IcAgain s={24} /> : <IcPlay s={24} />}</span>
      <div className="vmeta">
        <span className="vt">{state === "idle" || state === "done" ? a.duration || (total ? clock(total) : "") : `${clock(t)}${total ? ` / ${clock(total)}` : ""}`}</span>
        <span className="vl">{label}</span>
        {words ? (
          <button
            type="button"
            className={`vcc${captionsOn ? "" : " off"}`}
            aria-label="Captions"
            aria-pressed={captionsOn}
            onClick={(e) => {
              e.stopPropagation();
              toggleCaptions();
            }}
          >
            CC
          </button>
        ) : null}
      </div>
      <div className="vbar">
        <i style={{ width: total ? `${Math.min(100, (t / total) * 100)}%` : 0 }} />
      </div>
    </div>
  );
}

function FileBubble({ a }: { a: Attachment }): ReactNode {
  const href = fileHref(a);
  const ft = fileType(a.name);
  return (
    <a className="fbub" href={href ?? undefined} target="_blank" rel="noopener noreferrer" aria-label={`Open ${a.name}`}>
      <span className={`ftype t-${ft.ext}`}>{ft.badge}</span>
      <span className="fx">
        <b>{a.name}</b>
        <small>
          {ft.label}
          {a.size ? ` · ${a.size}` : ""}
        </small>
      </span>
      <span className="fdl">
        <IcDl s={16} />
      </span>
    </a>
  );
}

/**
 * The words a host's file is called by, as the host named it: the update's own title for its one file, the title
 * the host gave the file, else the file's name without its type.
 */
export function fileTitle(a: Attachment, title?: string | null): string {
  return title || a.title || a.name.replace(/\.[a-z0-9]{1,5}$/i, "") || a.name;
}

/**
 * A file the host sent, drawn as a paper page (the prototype's `card file`): their name, its title and the file's own
 * first lines, then its kind and pages, and Download, which says Saved after, as it does in Files.
 */
function FileCard({ a, title, saveKey }: { a: Attachment; title: string; saveKey: string }): ReactNode {
  const { host, saved, markSaved } = usePage();
  const href = fileHref(a);
  const ft = fileType(a.name);
  const { ref, facts } = usePaper(href, a.name, title);
  const done = saved(saveKey);
  const pages = facts?.pages ? ` · ${facts.pages} ${facts.pages === 1 ? "page" : "pages"}` : a.size ? ` · ${a.size}` : "";
  return (
    <div className="card file" ref={ref}>
      <div className="paper" aria-hidden="true">
        <div className="pk">{host.name}</div>
        <h4>{title}</h4>
        <ul>
          {(facts?.lines ?? []).slice(0, PAPER_LINES).map((line, i) => (
            // Why the index: a document can repeat a line.
            <li key={`${i}-${line}`}>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="fbar">
        <div className="ft">
          <b>{title}</b>
          <small>
            {ft.label}
            {pages}
          </small>
        </div>
        {href ? (
          <a className={`dl${done ? " saved" : ""}`} href={href} download aria-label={done ? `Saved. Download ${title} again` : `Download ${title}`} onClick={() => markSaved(saveKey)}>
            {done ? <IcCheck s={16} /> : <IcDl s={16} />}
            {done ? "Saved" : "Download"}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function Picture({ a, local }: { a: Attachment; local?: string | null }): ReactNode {
  const { view } = usePage();
  const url = local ?? fileHref(a);
  if (!url) return null;
  return (
    <button type="button" className="aimg" aria-label={`Open ${a.name}`} onClick={() => view({ url, name: a.name, meta: `Image${a.size ? ` · ${a.size}` : ""}`, video: false })}>
      <img src={url} alt={a.name} />
    </button>
  );
}

/**
 * A video the person picked, drawn as the prototype draws an attached video: its picture (its poster once it is
 * playable, the file itself until then), a dark tile while it has no picture yet, the play mark and the file's size.
 * A tap opens it full screen.
 */
function PickedVideo({ a, local }: { a: Attachment; local: string | null }): ReactNode {
  const { view } = usePage();
  // Why: a file the browser cannot draw (a phone's own format on a laptop) shows the dark tile, not a broken frame.
  const [unreadable, setUnreadable] = useState(false);
  const url = a.media_url ?? local;
  const picture = a.poster_url ? (
    <video poster={a.poster_url} muted playsInline preload="none" />
  ) : local && !unreadable ? (
    <video src={local} muted playsInline preload="metadata" onError={() => setUnreadable(true)} />
  ) : (
    <span className="avph" />
  );
  return (
    <button
      type="button"
      className="aimg avid"
      aria-label={`Play ${a.name}`}
      onClick={() => {
        if (url) view({ url, name: a.name, meta: `Video${a.size ? ` · ${a.size}` : ""}`, video: true });
      }}
    >
      {picture}
      <span className="avp">
        <IcPlay s={18} />
      </span>
      {a.size ? <span className="avs">{a.size}</span> : null}
    </button>
  );
}

function VideoFile({ a }: { a: Attachment }): ReactNode {
  const { view } = usePage();
  const url = fileHref(a);
  if (!url) return <FileBubble a={a} />;
  return (
    <button type="button" className="aimg avid" aria-label={`Play ${a.name}`} onClick={() => view({ url, name: a.name, meta: `Video${a.size ? ` · ${a.size}` : ""}`, video: true })}>
      <video src={url} muted playsInline preload="metadata" />
      <span className="avp">
        <IcPlay s={18} />
      </span>
      {a.size ? <span className="avs">{a.size}</span> : null}
    </button>
  );
}

function AttachmentView({ a, mine, local, file }: { a: Attachment; mine: boolean; local: string | null; file?: { title: string; saveKey: string } }): ReactNode {
  if (mine && isPickedVideo(a)) return <PickedVideo a={a} local={local} />;
  if (isVideoNote(a)) return <VideoNote a={a} mine={mine} local={local} />;
  if (a.kind === "image") return <Picture a={a} />;
  if (VIDEO_FILE.test(a.name)) return <VideoFile a={a} />;
  // The host's files are paper pages; the person's own stay the small file row they sent.
  if (!mine && file) return <FileCard a={a} title={file.title} saveKey={file.saveKey} />;
  return <FileBubble a={a} />;
}

/** What the bubble holds: words, a card, pictures, files, or a video note. */
export function Content({ entry }: { entry: Entry }): ReactNode {
  const { localMedia, localPicked, localFiles, data, playTalk } = usePage();
  const mine = entry.from === "me";
  const local = entry.outgoing ? localMedia(entry.outgoing.cid) : null;
  const picked = entry.outgoing ? localPicked(entry.outgoing.cid) : null;
  const pending = entry.outgoing && !entry.outgoing.sent ? localFiles(entry.outgoing.cid) : [];
  if (entry.post) {
    const p = entry.post;
    if (p.kind === "link" && p.link) {
      return (
        <a className="card link" href={p.link.url} target="_blank" rel="noopener noreferrer">
          <div className="link-top">
            <span className="fav">{(p.link.domain || "?").slice(0, 1).toUpperCase()}</span>
            <h4>{p.link.title || p.title || p.link.domain}</h4>
          </div>
          <div className="dom">
            <IcLink s={14} />
            {p.link.domain}
          </div>
        </a>
      );
    }
    // A file update's title is its one file's title, on the paper, so the words under it are the note alone.
    const titled = p.kind === "file" && p.attachments.length === 1 && Boolean(p.title);
    const words = [titled ? null : p.title, plainText(p.body_html)].filter(Boolean).join("\n");
    return (
      <>
        {p.attachments.map((a, i) => (
          <AttachmentView
            key={`${p.id}-${i}`}
            a={a}
            mine={false}
            local={null}
            file={{ title: fileTitle(a, titled ? p.title : null), saveKey: `${p.id}:${i}` }}
          />
        ))}
        {words ? (
          <div className="bub t">
            {words}
          </div>
        ) : null}
      </>
    );
  }
  const m: Pick<WatchMessage, "body" | "attachments"> = entry.message ?? { body: entry.comment?.body ?? "", attachments: entry.comment?.attachments };
  const atts = m.attachments ?? [];
  const quoted = entry.message?.reply_post;
  const answered = entry.message?.reply_answer;
  const at = entry.message?.at_sec;
  const roomTitle = data.sessions.find((s) => s.webinar_id === entry.webinar_id)?.title;
  const showLocalVideo = mine && local && atts.length === 0;
  return (
    <>
      {quoted ? (
        <div className="rpost">
          <IcReply s={14} />
          <span>{quoted.body}</span>
        </div>
      ) : null}
      {answered ? (
        <div className="qcard">
          <small>
            Your answer at {mmss(answered.at_sec)} · {answered.question}
          </small>
          <p>{answered.text}</p>
        </div>
      ) : null}
      {at === undefined ? null : (
        <span className="moment-row">
          <span className="moment">
            <IcPlay s={12} /> At {mmss(at)}
            {roomTitle ? ` · ${roomTitle}` : ""}
          </span>
          <button type="button" className="playlink" onClick={() => playTalk(entry.webinar_id, { start: true, at })}>
            Play from {mmss(at)}
          </button>
        </span>
      )}
      {showLocalVideo ? (
        picked ? (
          <PickedVideo a={{ kind: "video", name: picked.name, size: picked.size }} local={local} />
        ) : (
          <VideoNote a={{ kind: "video", name: "Video note" }} mine local={local} />
        )
      ) : null}
      {pending.map((c) =>
        c.kind === "image" && c.preview ? (
          <Picture key={c.id} a={{ kind: "image", name: c.name, size: c.size }} local={c.preview} />
        ) : (
          <FileBubble key={c.id} a={{ kind: "file", name: c.name, size: c.size }} />
        ),
      )}
      {atts.map((a, i) => (
        <AttachmentView key={`${entry.key}-${i}`} a={a} mine={mine} local={local} file={{ title: fileTitle(a), saveKey: `${entry.id}:${i}` }} />
      ))}
      {m.body ? <div className="bub t" dir="auto">{m.body}</div> : null}
    </>
  );
}

/** Wide rows hold a card rather than words. */
function isWide(e: Entry): boolean {
  if (e.post) return e.post.kind !== "note" || e.post.attachments.length > 0;
  const atts = e.message?.attachments ?? e.comment?.attachments ?? [];
  return atts.some((a) => isVideoNote(a) || a.kind === "file") || Boolean(e.outgoing && !e.message?.body);
}

const textOf = (e: Entry) => e.message?.body || e.comment?.body || (e.post?.kind === "note" ? plainText(e.post.body_html) : "");

interface RowProps {
  entry: Entry;
  first: boolean;
  last: boolean;
  lastMe?: boolean;
  inThread?: boolean;
  receipt?: ReactNode;
  selected: boolean;
  onSelect: (key: string | null) => void;
  onHold: (entry: Entry, el: HTMLElement) => void;
  /** The question's answers to tap, while it waits for one. */
  chips?: ReactNode;
}

export function Row({ entry, first, last, lastMe, inThread, receipt, selected, onSelect, onHold, chips }: RowProps): ReactNode {
  const { love, replyTo, isPinned, togglePin, toast, host, replying } = usePage();
  const from = entry.from === "host" ? "maya" : "me";
  // The message being answered wears a ring while the composer replies to it, as the prototype draws it.
  const target = !inThread && replying !== null && "entryKey" in replying && replying.entryKey === entry.key;
  const pinned = isPinned(entry);
  const loved = entry.from === "host" ? Boolean(entry.post?.liked ?? entry.message?.liked) : false;
  const hostLoved = entry.from === "me" && Boolean(entry.message?.host_liked);
  const text = textOf(entry);
  const press = useRef<number | undefined>(undefined);
  const held = useRef(false);
  const mw = useRef<HTMLDivElement>(null);
  const [pop, setPop] = useState(false);
  const wasLoved = useRef(loved);
  useEffect(() => {
    if (loved && !wasLoved.current) setPop(true);
    wasLoved.current = loved;
  }, [loved]);

  const copy = () => {
    try {
      void navigator.clipboard?.writeText(text).catch(() => {});
    } catch {
      /* the clipboard is not ours here */
    }
    toast("Copied");
  };
  const cls = [
    "row",
    from,
    isWide(entry) ? "wide" : "",
    first ? "gfirst" : "",
    last ? "glast" : "",
    lastMe ? "lastme" : "",
    loved || hostLoved ? "tbd" : "",
    pinned ? "pinned" : "",
    target ? "rt" : "",
    selected ? "sel" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cls}
      data-key={entry.key}
      onDoubleClick={(e) => {
        if (entry.from !== "host" || (e.target as Element).closest("button,a,.vnote")) return;
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        love(entry);
      }}
    >
      <div className="av">{entry.from === "host" ? <HostFace alt={host.first} /> : <MeFace />}</div>
      <div className="stack">
        <div
          className="mw"
          ref={mw}
          onPointerDown={(e) => {
            if ((e.target as Element).closest("button,a,input")) return;
            const x = e.clientX;
            const y = e.clientY;
            window.clearTimeout(press.current);
            press.current = window.setTimeout(() => {
              held.current = true;
              if (mw.current) onHold(entry, mw.current);
              try {
                navigator.vibrate?.(8);
              } catch {
                /* no vibration */
              }
            }, 460);
            const cancel = (ev: PointerEvent) => {
              if (ev.type === "pointermove" && Math.hypot(ev.clientX - x, ev.clientY - y) < 8) return;
              window.clearTimeout(press.current);
              window.removeEventListener("pointermove", cancel);
              window.removeEventListener("pointerup", cancel);
              window.removeEventListener("pointercancel", cancel);
            };
            window.addEventListener("pointermove", cancel);
            window.addEventListener("pointerup", cancel);
            window.addEventListener("pointercancel", cancel);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            window.clearTimeout(press.current);
            if (mw.current) onHold(entry, mw.current);
          }}
          onClick={(e) => {
            if (held.current) {
              held.current = false;
              e.preventDefault();
              return;
            }
            if ((e.target as Element).closest(".bub") && window.matchMedia("(hover: none)").matches) onSelect(selected ? null : entry.key);
          }}
        >
          <Content entry={entry} />
          {loved ? (
            <span className={`tb${pop ? " pop" : ""}`} aria-label="You loved this" onAnimationEnd={() => setPop(false)}>
              <IcHeartF s={14} />
            </span>
          ) : null}
          {hostLoved ? (
            <span className="tb" aria-label={`${host.first} loved this`}>
              <IcHeartF s={14} />
            </span>
          ) : null}
          {pinned ? (
            <span className="pinmark" aria-label="Pinned">
              <IcPin s={11} />
            </span>
          ) : null}
        </div>
        {!inThread ? chips : null}
        {entry.message?.edited ? <div className="edited">Edited</div> : null}
        {receipt}
      </div>
      {entry.outgoing && !entry.outgoing.sent ? null : (
        <div className="acts">
          {entry.from === "host" ? (
            <button type="button" className={`act${loved ? " on" : ""}`} aria-label="Love" onClick={() => love(entry)}>
              {loved ? <IcHeartF s={17} /> : <IcHeart s={17} />}
            </button>
          ) : null}
          <button
            type="button"
            className="act"
            aria-label="Reply"
            onClick={() => {
              replyTo(entry);
              onSelect(null);
            }}
          >
            <IcReply s={17} />
          </button>
          <button type="button" className={`act${pinned ? " on" : ""}`} aria-label={pinned ? "Unpin" : "Pin"} onClick={() => togglePin(entry)}>
            <IcPin s={16} />
          </button>
          {text ? (
            <button type="button" className="act" aria-label="Copy" onClick={copy}>
              <IcCopy s={16} />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The replies under a message or update, folded to "N replies" until opened. */
export function Replies({ entry, children }: { entry: Entry; children: ReactNode }): ReactNode {
  const { openThreads, toggleThread, host, me } = usePage();
  const n = entry.replies.length;
  if (!n) return null;
  const open = openThreads.has(entry.key);
  const froms = [...new Set(entry.replies.map((r) => r.from))];
  return (
    <div className={`ith${open ? " open" : ""}`} data-parent={entry.key}>
      <button type="button" className="ith-tog" aria-expanded={open} onClick={() => toggleThread(entry.key)}>
        <span className="ith-av">
          {froms.map((f) =>
            f === "host" ? (
              host.avatar ? <img key={f} src={host.avatar} alt="" /> : <i key={f}>{host.first.slice(0, 1)}</i>
            ) : me.avatar ? (
              <img key={f} src={me.avatar} alt="" />
            ) : (
              <i key={f}>{me.first.slice(0, 1)}</i>
            ),
          )}
        </span>
        {n} {n === 1 ? "reply" : "replies"}
        {open ? <IcCUp s={13} /> : <IcCDown s={13} />}
      </button>
      {open ? <div className="ith-list">{children}</div> : null}
    </div>
  );
}
